import React from 'react';
import PropTypes from 'prop-types';

import { getTranslation } from 'common/react/utils/translations';

import { FixtureLinkButton } from './FixtureLinkButton';
import { FixtureMatchReportLink } from './FixtureMatchReportLink';

export const FixtureLinks = ({
    match,
    matchStatus,
    metadata = {},
    broadcastGuideUrl,
    reportLabel
}) => {
    const hasFreeEntry = metadata.free_entry === 'true' ? true : false,
        isSoldOut = metadata.sold_out === 'true';

    const compAbbr = PULSE.app.common.match.getCompName(
        match.compSeason.providerId
    );

    const renderUpcomingTicketLinks = () => {
        let ticketLink = {};

        if (metadata.ticket_link && !(hasFreeEntry || isSoldOut)) {
            ticketLink = {
                icon: 'tickets-new',
                label: getTranslation('label.fixtures.buy-tickets'),
                href: metadata.ticket_link,
                target: '_blank',
                cssClass: 'fixtures__link-button--alt'
            };

            return (
                <FixtureLinkButton
                    {...ticketLink}
                    match={match}
                    onClick={() => {
                        PULSE.app.tracking.selectFixtureCardCtaHandler(
                            getTranslation('label.fixtures.buy-tickets'),
                            metadata?.ticket_link,
                            moment(match?.utcStartTime).format('dddd MMMM D'),
                            PULSE.app.common.url.isExternalUrl(
                                metadata?.ticket_link
                            ),
                            compAbbr.toLowerCase()
                        );
                    }}
                />
            );
        }
    };

    const renderUpcomingOrLiveTicketLinks = () => {
        let broadcastGuideLink = {};
        let playerStatsLink = {};

        // only show a broadcast guide link if the match is assigned to a broadcaster match-event
        if (
            broadcastGuideUrl &&
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        ) {
            broadcastGuideLink = {
                icon: 'live-broadcast',
                label: getTranslation('label.fixtures.broadcastGuide'),
                href: broadcastGuideUrl,
                target: '_blank',
                theme: 'live'
            };

            playerStatsLink = {
                icon: 'player-stats',
                label: getTranslation('label.fixtures.player-stats'),
                href:
                    PULSE.app.common.url.getMatchURL(match.id) +
                    '#player-stats',
                target: '_blank',
                theme: 'live'
            };

            return (
                <>
                    <FixtureLinkButton
                        {...playerStatsLink}
                        match={match}
                        onClick={() => {
                            PULSE.app.tracking.selectFixtureCardCtaHandler(
                                getTranslation('label.fixtures.player-stats'),
                                playerStatsLink?.href,
                                moment(match?.utcStartTime).format(
                                    'dddd MMMM D'
                                ),
                                PULSE.app.common.url.isExternalUrl(
                                    playerStatsLink?.href
                                ),
                                compAbbr.toLowerCase()
                            );
                        }}
                    />
                    <FixtureLinkButton
                        {...broadcastGuideLink}
                        match={match}
                        onClick={() => {
                            PULSE.app.tracking.selectFixtureCardCtaHandler(
                                getTranslation('label.fixtures.broadcastGuide'),
                                broadcastGuideUrl,
                                moment(match?.utcStartTime).format(
                                    'dddd MMMM D'
                                ),
                                PULSE.app.common.url.isExternalUrl(
                                    broadcastGuideUrl
                                ),
                                compAbbr.toLowerCase()
                            );
                        }}
                    />
                </>
            );
        }

        // only show a broadcast guide link if the match is assigned to a broadcaster match-event
        if (
            broadcastGuideUrl &&
            matchStatus !== PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        ) {
            broadcastGuideLink = {
                icon: 'live-broadcast',
                label: getTranslation('label.fixtures.broadcastGuide'),
                href: broadcastGuideUrl,
                target: '_blank'
            };

            return (
                <FixtureLinkButton
                    {...broadcastGuideLink}
                    match={match}
                    onClick={() => {
                        PULSE.app.tracking.selectFixtureCardCtaHandler(
                            getTranslation('label.fixtures.broadcastGuide'),
                            broadcastGuideUrl,
                            moment(match?.utcStartTime).format('dddd MMMM D'),
                            PULSE.app.common.url.isExternalUrl(
                                broadcastGuideUrl
                            ),
                            compAbbr.toLowerCase()
                        );
                    }}
                />
            );
        }

        // show Player Stats button if no broadcast guide, stream exists and match is live
        if (
            !broadcastGuideUrl &&
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        ) {
            playerStatsLink = {
                icon: 'player-stats',
                label: getTranslation('label.fixtures.player-stats'),
                href:
                    PULSE.app.common.url.getMatchURL(match.id) +
                    '#player-stats',
                target: '_blank',
                theme: 'live'
            };

            return (
                <FixtureLinkButton
                    {...playerStatsLink}
                    match={match}
                    onClick={() => {
                        PULSE.app.tracking.selectFixtureCardCtaHandler(
                            getTranslation('label.fixtures.player-stats'),
                            playerStatsLink?.href,
                            moment(match?.utcStartTime).format('dddd MMMM D'),
                            PULSE.app.common.url.isExternalUrl(
                                playerStatsLink?.href
                            ),
                            compAbbr.toLowerCase()
                        );
                    }}
                />
            );
        }
    };

    const renderUpcomingTravelLinks = () => {
        let travelLink = {};

        if (metadata.travel_link) {
            travelLink = {
                icon: 'plane',
                label: getTranslation('label.fixtures.travel'),
                href: metadata.travel_link,
                target: '_blank'
            };

            return (
                <FixtureLinkButton
                    {...travelLink}
                    match={match}
                    onClick={() => {
                        PULSE.app.tracking.selectFixtureCardCtaHandler(
                            getTranslation('label.fixtures.travel'),
                            metadata?.travel_link,
                            moment(match?.utcStartTime).format('dddd MMMM D'),
                            PULSE.app.common.url.isExternalUrl(
                                metadata?.travel_link
                            ),
                            compAbbr.toLowerCase()
                        );
                    }}
                />
            );
        }
    };

    return (
        <>
            {matchStatus ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && ( // check match status
                <FixtureMatchReportLink
                    match={match}
                    reportLabel={reportLabel}
                />
            )}

            {matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING &&
                renderUpcomingTicketLinks()}

            {(matchStatus ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
                matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE) &&
                renderUpcomingOrLiveTicketLinks()}

            {matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING &&
                renderUpcomingTravelLinks()}
        </>
    );
};

FixtureLinks.propTypes = {
    match: PropTypes.object,
    matchStatus: PropTypes.string,
    metadata: PropTypes.object,
    reportLabel: PropTypes.string,
    broadcastGuideUrl: PropTypes.string,
    index: PropTypes.number
};
